import { useState } from 'react'
import { 
  Container,
  VStack,
  Heading,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Box,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  HStack,
  Icon,
  Badge,
  Divider,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Avatar,
  Wrap,
  WrapItem,
  IconButton,
  Tooltip,
  Link
} from '@chakra-ui/react'
import { ChevronRightIcon, SearchIcon, ExternalLinkIcon } from '@chakra-ui/icons'
import { Link as RouterLink } from 'react-router-dom'
import { 
  FiBook, 
  FiMessageCircle, 
  FiMail, 
  FiPhone, 
  FiVideo, 
  FiDownload,
  FiHelpCircle,
  FiLifeBuoy,
  FiUsers,
  FiClock,
  FiCheck,
  FiStar,
  FiArrowRight
} from 'react-icons/fi'

const Help = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [filteredFAQs, setFilteredFAQs] = useState([])
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    category: '',
    priority: 'medium',
    description: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const cardBg = useColorModeValue('white', 'gray.700')
  const searchBg = useColorModeValue('gray.50', 'gray.800')

  // FAQ Data
  const faqData = [
    {
      id: 1,
      category: 'Getting Started',
      question: 'How do I create my first content entry?',
      answer: 'To create your first content entry, navigate to the Content section in the sidebar, click "Create New", select your content type, and fill out the required fields. Make sure to save your work before publishing.'
    },
    {
      id: 2,
      category: 'API Keys',
      question: 'How do I generate an API key?',
      answer: 'Go to Settings > API Keys, click "Create New API Key", enter a name and select permissions. Choose between sandbox and production modes based on your needs.'
    },
    {
      id: 3,
      category: 'Sites',
      question: 'How do I add a new site to my account?',
      answer: 'Navigate to Sites in the main menu, click "Create New Site", enter your site details including name and domain, then configure your locales and content types.'
    },
    {
      id: 4,
      category: 'Locales',
      question: 'How do I set up multiple languages?',
      answer: 'Go to Settings > Locales, create locale entries for each language you want to support, then assign them to your sites. You can then create content in multiple languages.'
    },
    {
      id: 5,
      category: 'Billing',
      question: 'How do I upgrade my plan?',
      answer: 'Visit the Billing section in your account settings to view available plans and upgrade options. Changes take effect immediately.'
    },
    {
      id: 6,
      category: 'Troubleshooting',
      question: 'Why am I getting API errors?',
      answer: 'Check your API key permissions, rate limits, and ensure you\'re using the correct endpoints. Verify your request format matches our API documentation.'
    }
  ]

  // Support categories
  const supportCategories = [
    {
      icon: FiBook,
      title: 'Documentation',
      description: 'Comprehensive guides and API references',
      color: 'blue',
      action: 'Browse Docs',
      link: '#'
    },
    {
      icon: FiVideo,
      title: 'Video Tutorials',
      description: 'Step-by-step video guides',
      color: 'purple',
      action: 'Watch Videos',
      link: '#'
    },
    {
      icon: FiMessageCircle,
      title: 'Live Chat',
      description: 'Chat with our support team',
      color: 'green',
      action: 'Start Chat',
      available: true
    },
    {
      icon: FiMail,
      title: 'Email Support',
      description: 'Get help via email',
      color: 'orange',
      action: 'Send Email',
      link: 'mailto:support@cms.com'
    }
  ]

  // Team members
  const supportTeam = [
    {
      name: 'Sarah Johnson',
      role: 'Senior Support Engineer',
      avatar: 'https://bit.ly/sage-adebayo',
      specialties: ['API Integration', 'Technical Issues']
    },
    {
      name: 'Mike Chen',
      role: 'Support Specialist',
      avatar: 'https://bit.ly/kent-c-dodds',
      specialties: ['Content Management', 'Getting Started']
    },
    {
      name: 'Emma Wilson',
      role: 'Customer Success Manager',
      avatar: 'https://bit.ly/ryan-florence',
      specialties: ['Account Management', 'Best Practices']
    }
  ]

  // Handle FAQ search
  const handleSearch = (query) => {
    setSearchQuery(query)
    if (query.trim() === '') {
      setFilteredFAQs([])
      return
    }

    const filtered = faqData.filter(faq =>
      faq.question.toLowerCase().includes(query.toLowerCase()) ||
      faq.answer.toLowerCase().includes(query.toLowerCase()) ||
      faq.category.toLowerCase().includes(query.toLowerCase())
    )
    setFilteredFAQs(filtered)
  }

  // Handle support ticket submission
  const handleTicketSubmit = async () => {
    if (isSubmitting) return
    
    setIsSubmitting(true)
    
    // Simulate API call
    setTimeout(() => {
      toast({
        title: 'Support ticket created',
        description: 'We\'ll get back to you within 24 hours',
        status: 'success',
        duration: 5000
      })
      setIsSubmitting(false)
      onClose()
      setTicketForm({
        subject: '',
        category: '',
        priority: 'medium',
        description: ''
      })
    }, 2000)
  }

  const displayFAQs = searchQuery ? filteredFAQs : faqData

  return (
    <Container maxW="container.2xl" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Help & Support</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={8} align="stretch">
        {/* Hero Section */}
        <Box textAlign="center" py={8}>
          <Heading size="2xl" mb={4}>How can we help you?</Heading>
          <Text fontSize="lg" color="gray.500" mb={8} maxW="600px" mx="auto">
            Find answers to common questions, browse our documentation, or contact our support team
          </Text>
          
          {/* Search Bar */}
          <InputGroup maxW="500px" mx="auto">
            <InputLeftElement>
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search for help topics..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              bg={searchBg}
              size="lg"
              borderRadius="full"
            />
          </InputGroup>
        </Box>

        {/* Support Categories */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          {supportCategories.map((category, index) => (
            <Card
              key={index}
              bg={cardBg}
              borderColor={borderColor}
              variant="outline"
              _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
              transition="all 0.2s"
              cursor="pointer"
            >
              <CardBody textAlign="center" p={6}>
                <VStack spacing={4}>
                  <Box
                    p={3}
                    borderRadius="full"
                    bg={`${category.color}.100`}
                    color={`${category.color}.500`}
                  >
                    <Icon as={category.icon} boxSize={6} />
                  </Box>
                  <VStack spacing={2}>
                    <Heading size="md">{category.title}</Heading>
                    <Text color="gray.500" fontSize="sm" textAlign="center">
                      {category.description}
                    </Text>
                  </VStack>
                  <HStack>
                    <Button
                      colorScheme={category.color}
                      variant="outline"
                      size="sm"
                      rightIcon={category.link ? <ExternalLinkIcon /> : <FiArrowRight />}
                      as={category.link ? Link : Button}
                      href={category.link}
                      isExternal={!!category.link}
                      onClick={category.title === 'Live Chat' ? onOpen : undefined}
                    >
                      {category.action}
                    </Button>
                    {category.available && (
                      <Badge colorScheme="green" variant="subtle">Online</Badge>
                    )}
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>

        {/* Quick Stats */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} textAlign="center">
              <VStack>
                <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                  <Icon as={FiClock} mr={2} />
                  &lt; 4 hours
                </Text>
                <Text color="gray.500" fontSize="sm">Average response time</Text>
              </VStack>
              <VStack>
                <Text fontSize="2xl" fontWeight="bold" color="green.500">
                  <Icon as={FiCheck} mr={2} />
                  98%
                </Text>
                <Text color="gray.500" fontSize="sm">Issue resolution rate</Text>
              </VStack>
              <VStack>
                <Text fontSize="2xl" fontWeight="bold" color="purple.500">
                  <Icon as={FiStar} mr={2} />
                  4.9/5
                </Text>
                <Text color="gray.500" fontSize="sm">Customer satisfaction</Text>
              </VStack>
              <VStack>
                <Text fontSize="2xl" fontWeight="bold" color="orange.500">
                  <Icon as={FiLifeBuoy} mr={2} />
                  24/7
                </Text>
                <Text color="gray.500" fontSize="sm">Support availability</Text>
              </VStack>
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* FAQ Section */}
        <Box>
          <HStack justify="space-between" align="center" mb={6}>
            <Box>
              <Heading size="lg" mb={2}>Frequently Asked Questions</Heading>
              <Text color="gray.500">
                {searchQuery 
                  ? `${displayFAQs.length} results for "${searchQuery}"`
                  : 'Common questions and answers to get you started'
                }
              </Text>
            </Box>
            {searchQuery && (
              <Button
                variant="ghost"
                onClick={() => handleSearch('')}
                size="sm"
              >
                Clear search
              </Button>
            )}
          </HStack>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody p={0}>
              <Accordion allowToggle>
                {displayFAQs.map((faq, index) => (
                  <AccordionItem key={faq.id} border="none">
                    <AccordionButton p={6} _hover={{ bg: 'gray.50' }}>
                      <HStack flex="1" textAlign="left" spacing={4}>
                        <Badge colorScheme="blue" variant="subtle">
                          {faq.category}
                        </Badge>
                        <Text fontWeight="medium">{faq.question}</Text>
                      </HStack>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={6} px={6}>
                      <Text color="gray.600" lineHeight="tall">
                        {faq.answer}
                      </Text>
                    </AccordionPanel>
                    {index < displayFAQs.length - 1 && <Divider />}
                  </AccordionItem>
                ))}
              </Accordion>

              {displayFAQs.length === 0 && searchQuery && (
                <Box p={8} textAlign="center">
                  <Text color="gray.500" mb={4}>
                    No results found for "{searchQuery}"
                  </Text>
                  <Button variant="outline" onClick={onOpen}>
                    Contact Support
                  </Button>
                </Box>
              )}
            </CardBody>
          </Card>
        </Box>

        {/* Support Team */}
        <Box>
          <Heading size="lg" mb={6}>Meet Our Support Team</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {supportTeam.map((member, index) => (
              <Card key={index} bg={cardBg} borderColor={borderColor} variant="outline">
                <CardBody textAlign="center" p={6}>
                  <VStack spacing={4}>
                    <Avatar size="lg" src={member.avatar} name={member.name} />
                    <VStack spacing={1}>
                      <Text fontWeight="bold">{member.name}</Text>
                      <Text color="gray.500" fontSize="sm">{member.role}</Text>
                    </VStack>
                    <Wrap justify="center" spacing={1}>
                      {member.specialties.map((specialty, idx) => (
                        <WrapItem key={idx}>
                          <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                            {specialty}
                          </Badge>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Box>

        {/* Contact Section */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody>
            <VStack spacing={6} textAlign="center">
              <Box>
                <Heading size="md" mb={2}>Still need help?</Heading>
                <Text color="gray.500">
                  Can't find what you're looking for? Our support team is here to help.
                </Text>
              </Box>
              
              <HStack spacing={4}>
                <Button
                  leftIcon={<FiMessageCircle />}
                  colorScheme="blue"
                  onClick={onOpen}
                >
                  Create Support Ticket
                </Button>
                <Button
                  leftIcon={<FiMail />}
                  variant="outline"
                  as={Link}
                  href="mailto:support@cms.com"
                  isExternal
                >
                  Email Us
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Additional Resources */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody p={6}>
              <VStack align="start" spacing={4}>
                <HStack>
                  <Icon as={FiDownload} color="blue.500" />
                  <Heading size="sm">Resources</Heading>
                </HStack>
                <VStack align="start" spacing={2}>
                  <Link color="blue.500" href="#" isExternal>
                    API Documentation <ExternalLinkIcon mx="2px" />
                  </Link>
                  <Link color="blue.500" href="#" isExternal>
                    Getting Started Guide <ExternalLinkIcon mx="2px" />
                  </Link>
                  <Link color="blue.500" href="#" isExternal>
                    Video Tutorials <ExternalLinkIcon mx="2px" />
                  </Link>
                  <Link color="blue.500" href="#" isExternal>
                    Status Page <ExternalLinkIcon mx="2px" />
                  </Link>
                </VStack>
              </VStack>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody p={6}>
              <VStack align="start" spacing={4}>
                <HStack>
                  <Icon as={FiUsers} color="green.500" />
                  <Heading size="sm">Community</Heading>
                </HStack>
                <VStack align="start" spacing={2}>
                  <Link color="blue.500" href="#" isExternal>
                    Developer Forum <ExternalLinkIcon mx="2px" />
                  </Link>
                  <Link color="blue.500" href="#" isExternal>
                    Discord Community <ExternalLinkIcon mx="2px" />
                  </Link>
                  <Link color="blue.500" href="#" isExternal>
                    Feature Requests <ExternalLinkIcon mx="2px" />
                  </Link>
                  <Link color="blue.500" href="#" isExternal>
                    Blog & Updates <ExternalLinkIcon mx="2px" />
                  </Link>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>
      </VStack>

      {/* Support Ticket Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent>
          <ModalHeader>Create Support Ticket</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  Our support team typically responds within 4 hours during business days.
                </Text>
              </Alert>

              <FormControl isRequired>
                <FormLabel>Subject</FormLabel>
                <Input
                  value={ticketForm.subject}
                  onChange={(e) => setTicketForm({...ticketForm, subject: e.target.value})}
                  placeholder="Brief description of your issue"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Category</FormLabel>
                <Select
                  value={ticketForm.category}
                  onChange={(e) => setTicketForm({...ticketForm, category: e.target.value})}
                  placeholder="Select a category"
                >
                  <option value="technical">Technical Issue</option>
                  <option value="billing">Billing Question</option>
                  <option value="feature">Feature Request</option>
                  <option value="account">Account Management</option>
                  <option value="other">Other</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Priority</FormLabel>
                <Select
                  value={ticketForm.priority}
                  onChange={(e) => setTicketForm({...ticketForm, priority: e.target.value})}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={ticketForm.description}
                  onChange={(e) => setTicketForm({...ticketForm, description: e.target.value})}
                  placeholder="Please provide detailed information about your issue..."
                  rows={6}
                />
              </FormControl>

              <HStack justify="flex-end" pt={4}>
                <Button variant="ghost" onClick={onClose} isDisabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleTicketSubmit}
                  isDisabled={!ticketForm.subject || !ticketForm.category || !ticketForm.description}
                  isLoading={isSubmitting}
                  loadingText="Creating..."
                >
                  Create Ticket
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Container>
  )
}

export default Help